import logoSeapointName from '../../../../assets/images/logo-seapoint-name.svg';
import visaLogo from '../../../../assets/images/visa-logo.svg';
import whiteBackground from '../../../../assets/images/white-background.png';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { MaskedValue } from './masked-value';

type CardPreviewProps = {
  className?: string;
  cardholderName?: string;
  pan?: string;
  expiry?: string;
  cvv2?: string;
  isLoading?: boolean;
  onReveal?: () => void;
  onCopyPan?: () => void;
  onCopyExpiry?: () => void;
  onCopyCvv2?: () => void;
};

export const CardPreview = ({
  className,
  cardholderName,
  pan,
  expiry,
  cvv2,
  isLoading,
  onReveal,
  onCopyPan,
  onCopyExpiry,
  onCopyCvv2,
}: CardPreviewProps) => {
  const isCardDetailsMode = !!pan;
  const isPanRevealed = !!(pan && pan.length > 4);
  const isExpiryRevealed = !!expiry;
  const isCvv2Revealed = !!cvv2;

  const formatPan = () => {
    if (!pan) return null;
    if (isPanRevealed) {
      return pan.match(/.{1,4}/g)?.join(' ') || pan;
    }
    return `•••• •••• •••• ${pan}`;
  };

  return (
    <div
      className={cn(
        'relative h-[180px] w-[286px] overflow-hidden rounded-xl border border-black/10 bg-white shadow-lg',
        className,
      )}
    >
      <img
        src={whiteBackground}
        alt=""
        loading="eager"
        fetchpriority="high"
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div className="relative z-10 flex h-full flex-col justify-between p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <img
              src={logoSeapointName}
              alt="Seapoint logo"
              loading="eager"
              fetchpriority="high"
              className="h-5 w-auto"
            />
          </div>

          <Stack space={0.5} align="end">
            <span className="text-sm font-semibold text-black/90">Virtual Card</span>
            <span className="text-[9px] font-semibold text-black/70">Issued by Modulr</span>
          </Stack>
        </div>

        <div className="flex flex-col gap-3">
          {cardholderName && !isCardDetailsMode ? (
            <div className="mt-auto flex items-end justify-between">
              <p className="max-w-[180px] truncate text-sm font-medium tracking-wide text-black/90">{cardholderName}</p>
              <img src={visaLogo} alt="Visa" loading="eager" fetchpriority="high" className="h-4 w-auto" />
            </div>
          ) : (
            <>
              <MaskedValue
                value={formatPan()}
                isRevealed={isPanRevealed}
                isLoading={isLoading}
                onReveal={onReveal}
                onCopy={onCopyPan}
                className="text-base font-medium tracking-widest text-black/90"
                aria-label="Card number"
              />

              <div className="flex items-end justify-between">
                <div className="flex items-center gap-4 text-xs font-medium text-black/60">
                  <div className="flex items-center gap-1">
                    <span>Exp</span>
                    <MaskedValue
                      value={expiry}
                      maskedValue="••/••"
                      isRevealed={isExpiryRevealed}
                      isLoading={isLoading}
                      onReveal={onReveal}
                      onCopy={onCopyExpiry}
                      className="font-semibold text-black/80"
                      aria-label="Expiry date"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span>CVC</span>
                    <MaskedValue
                      value={cvv2}
                      maskedValue="•••"
                      isRevealed={isCvv2Revealed}
                      isLoading={isLoading}
                      onReveal={onReveal}
                      onCopy={onCopyCvv2}
                      className="font-semibold text-black/80"
                      aria-label="Security code"
                    />
                  </div>
                </div>

                <img src={visaLogo} alt="Visa" loading="eager" fetchpriority="high" className="h-4 w-auto" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
